"use client";//Not needed for now but if we update it will protect us from using the queryClient wrong
import Btn, { BtnModifier } from 'components/btn';
import Header, { HeaderModifier } from 'components/header';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PlusIcon from 'assets/icons/plus.svg';
import { useTranslation } from 'react-i18next';
import Alert from 'components/alert';

import ValidationDepositWithdrawalTable from 'components/table/validation/validationDepositWithdrawalTable';
import ValidationDuplicateTable from 'components/table/validation/validationDuplicateTable';
import ValidationFlowNegativesTable from 'components/table/validation/validationFlowNegativesTable';
import ValidationNoticeTable from 'components/table/validation/validationNoticeTable';
import ValidationWarningTable from 'components/table/validation/validationWarningTable';
import {
  type MRT_RowSelectionState,
} from 'material-react-table';
import { ValidationSelectOption, ValidationSectionId } from 'models/validation';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ValidationTableFilters from 'components/table/filters/validationTableFilters';
import { ValidationSelectionCount } from './components/validationSectionSelector';

import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);//Needed for MUI fields

const queryClient = new QueryClient({
  defaultOptions: { queries: { 
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    //use cached data for same request in a 15sec interval, send request to network only after that time
    staleTime: 15000,
  }}
});

function ValidationComponent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [hasError, setHasError] = useState(false);
  const [isLoadingSuccess, setIsLoadingSuccess] = useState<boolean>(false);

  const [isFetching, setIsFetching] = useState(false);
  const [buttonSelection, setButtonSelection] = useState(ValidationSectionId.DEPOSITWITHDRAWAL);
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [count, setCount] = useState<ValidationSelectionCount[]>([]);

  //old
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});//not used for deposit table, refactor out
  const isDeletebuttonDisabled = isFetching || Object.keys(rowSelection).length < 1;//not used for deposit table, refactor out

  //select options
  const statusSelectOption: ValidationSelectOption[] = [
    { text: t('Matched', { ns: 'validation' }), value: 1 },
    { text: t('Unmatched', { ns: 'validation' }), value: 2 },
    { text: t('Duplicate', { ns: 'validation' }), value: 3 },
    { text: t('Warning', { ns: 'validation' }), value: 4 },
    { text: t('Notice', { ns: 'validation' }), value: 5 },
    { text: t('Flow Negatives', { ns: 'validation' }), value: 6 },
  ];

  const transactionTypeSelectOption: ValidationSelectOption[] = [
    { text: t('Trade', { ns: 'validation' }), value: 0 },
    { text: t('Transfer', { ns: 'validation' }), value: -1 },
    { text: t('Deposit', { ns: 'validation' }), value: 1 },
    { text: t('Withdrawal', { ns: 'validation' }), value: 2 },
    { text: t('Income', { ns: 'validation' }), value: 3 },
    { text: t('Mining', { ns: 'validation' }), value: 4 },
    { text: t('Gift/Tip', { ns: 'validation' }), value: 5 },
    { text: t('Spend', { ns: 'validation' }), value: 6 },
    { text: t('Donation', { ns: 'validation' }), value: 7 },
    { text: t('Gift', { ns: 'validation' }), value: 8 },
    { text: t('Stolen', { ns: 'validation' }), value: 9 },
    { text: t('Lost', { ns: 'validation' }), value: 10 },
    { text: t('Margin Loss', { ns: 'validation' }), value: 11 },
    { text: t('Margin Fee', { ns: 'validation' }), value: 12 },
    { text: t('Borrowing Fee', { ns: 'validation' }), value: 13 },
    { text: t('Settlement Fee', { ns: 'validation' }), value: 14 },
    { text: t('Derivatives / Futures Loss', { ns: 'validation' }), value: 15 },
    { text: t('Other Fee', { ns: 'validation' }), value: 16 },
    { text: t('Provide Liquidity', { ns: 'validation' }), value: 17 },
    { text: t('Return LP Token', { ns: 'validation' }), value: 18 },
    { text: t('Collateral Liquidation', { ns: 'validation' }), value: 20 },
    { text: t('Other Expense', { ns: 'validation' }), value: 40 },
    { text: t('Expense (non taxable)', { ns: 'validation' }), value: 41 },
    { text: t('Loan Debt (non taxable)', { ns: 'validation' }), value: 42 },
    { text: t('Airdrop', { ns: 'validation' }), value: 50 },
    { text: t('Staking', { ns: 'validation' }), value: 51 },
    { text: t('Masternode', { ns: 'validation' }), value: 52 },
    { text: t('Minting', { ns: 'validation' }), value: 53 },
    { text: t('Dividends Income', { ns: 'validation' }), value: 54 },
    { text: t('Lending Income', { ns: 'validation' }), value: 55 },
    { text: t('Interest Income', { ns: 'validation' }), value: 56 },
    { text: t('Reward / Bonus', { ns: 'validation' }), value: 57 },
    { text: t('Mining (commercial)', { ns: 'validation' }), value: 58 },
    { text: t('Margin Profit', { ns: 'validation' }), value: 59 },
    { text: t('Derivatives / Futures Profit', { ns: 'validation' }), value: 60 },
    { text: t('Other Income', { ns: 'validation' }), value: 90 },
    { text: t('Income (non taxable)', { ns: 'validation' }), value: 91 },
    { text: t('Other Income (non taxable)', { ns: 'validation' }), value: 92 },
    { text: t('Airdrop (non taxable)', { ns: 'validation' }), value: 93 },
    { text: t('Rebate (non taxable)', { ns: 'validation' }), value: 94 },
    { text: t('Loan (non taxable)', { ns: 'validation' }), value: 95 },
    { text: t('Loan Repay (non taxable)', { ns: 'validation' }), value: 96 },
    { text: t('Remove Liquidity', { ns: 'validation' }), value: 97 },
    { text: t('Receive LP Token', { ns: 'validation' }), value: 98 },
    { text: t('Margin Trade', { ns: 'validation' }), value: 100 },
    { text: t('Derivatives / Futures Trade', { ns: 'validation' }), value: 101 },
  ];

  const setCountFromResponse = (countValue: number | null, item: ValidationSectionId) => {
      setCount(prev => {
        const hasItem = prev.find((e) => e.itemId === item);
          if (hasItem) {
            return prev.map((e) => e.itemId === item ? { ...e, count: countValue } : e)
          } else {
            return [
              ...prev,
              {
                itemId: item,
                count: countValue,
              }
            ];
          }
      });
  };

  return (
    <QueryClientProvider client={queryClient}>
      <div className="ct_padding">
        <div className="ct_section _oldView">
          <div className="ct_section-inner">
            <div className="ct_section-main">
              <div className="ct_validationHeader">
                <div className="ct_validationHeader-main">
                  <Header
                    headline={t('Review Transactions', { ns: 'validation' })}
                    desc={t('This overview provides a list of your current validation results.', { ns: 'validation' })}
                    modifiers={[HeaderModifier.OLD, HeaderModifier.OLD_MARGIN]}
                  />
                </div>
                <div className="ct_overviewHeader-aside">
                  <Btn onClick={() => navigate('/import')} icon={<PlusIcon />} type="primary" text={t('Add Import', { ns: 'imports' })} modifier={[BtnModifier.OLD]} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="ct_section _oldView">
        <div className="ct_section-inner">
          <div className="ct_validationHeader-fullSide">
          <ValidationTableFilters 
            isLoadingSuccess={isLoadingSuccess}
            isFetching={isFetching}
            setButtonSelection={setButtonSelection}
            buttonSelection={buttonSelection}
            searchCb={(searched) => { 
              setGlobalSearch(searched); 
            }}
            count={count}
            setIsFetching={setIsFetching}
            setCountFromResponse={setCountFromResponse} 
            globalSearch={globalSearch}
          />
            {!!hasError && (
            <Alert type="error" hasArrow={false} title={t('Error')}>
              <>
                {t('No transactions found.')}
                {' '}
                <Link to="/import" className="ct_link _bold">
                  {t('Start importing your trades here', { ns: 'partner' })}
                </Link>
                .
              </>
            </Alert>
            )}
            {!hasError && (
              <>
              {buttonSelection && buttonSelection === ValidationSectionId.DEPOSITWITHDRAWAL && (
                <ValidationDepositWithdrawalTable
                  statusSelectOption={statusSelectOption}
                  transactionTypeSelectOption={transactionTypeSelectOption}
                  globalSearch={globalSearch}
                  isFetching={isFetching}
                  setIsFetching={setIsFetching}
                  rowSelection={rowSelection}
                  setRowSelection={setRowSelection}
                  onLoadCb={(info) => {
                    if (info) {
                      setIsLoadingSuccess(true);
                      setHasError(false);
                      setCountFromResponse(info.meta.total_item_count, ValidationSectionId.DEPOSITWITHDRAWAL);
                    } else {
                      setIsLoadingSuccess(false);
                      setHasError(true);
                      setCountFromResponse(null, ValidationSectionId.DEPOSITWITHDRAWAL);
                    }
                  }
                }
                alreadyMatchedOnly={false}
                />
              )}
              {buttonSelection && buttonSelection === ValidationSectionId.DUPLICATE && (
               <ValidationDuplicateTable
                  statusSelectOption={statusSelectOption}
                  transactionTypeSelectOption={transactionTypeSelectOption}
                  globalSearch={globalSearch}
                  isFetching={isFetching}
                  setIsFetching={setIsFetching}
                  rowSelection={rowSelection}
                  setRowSelection={setRowSelection}
                  onLoadCb={(info) => {
                    if (info) {
                      setIsLoadingSuccess(true);
                      setHasError(false);
                      setCountFromResponse(info.meta.total_item_count, ValidationSectionId.DUPLICATE);
                    } else {
                      setIsLoadingSuccess(false);
                      setHasError(true);
                      setCountFromResponse(null, ValidationSectionId.DUPLICATE);
                    }
                  }
                }
                />
              )}
              {buttonSelection && buttonSelection === ValidationSectionId.NEGATIVE && (
                <ValidationFlowNegativesTable
                  statusSelectOption={statusSelectOption}
                  transactionTypeSelectOption={transactionTypeSelectOption}
                  globalSearch={globalSearch}
                  isFetching={isFetching}
                  setIsFetching={setIsFetching}
                  rowSelection={rowSelection}
                  setRowSelection={setRowSelection}
                  onLoadCb={(info) => {
                    if (info) {
                      setIsLoadingSuccess(true);
                      setHasError(false);
                      setCountFromResponse(info.meta.total_item_count, ValidationSectionId.NEGATIVE);
                    } else {
                      setIsLoadingSuccess(false);
                      setHasError(true);
                      setCountFromResponse(null, ValidationSectionId.NEGATIVE);
                    }
                  }
                }
                />
              )}
              {buttonSelection && buttonSelection === ValidationSectionId.NOTICE && (
                 <ValidationNoticeTable
                  statusSelectOption={statusSelectOption}
                  transactionTypeSelectOption={transactionTypeSelectOption}
                  globalSearch={globalSearch}
                  isFetching={isFetching}
                  setIsFetching={setIsFetching}
                  rowSelection={rowSelection}
                  setRowSelection={setRowSelection}
                  onLoadCb={(info) => {
                    if (info) {
                      setIsLoadingSuccess(true);
                      setHasError(false);
                      setCountFromResponse(info.meta.total_item_count, ValidationSectionId.NOTICE);
                    } else {
                      setIsLoadingSuccess(false);
                      setHasError(true);
                      setCountFromResponse(null, ValidationSectionId.NOTICE);
                    }
                  }
                }
                />
              )}
              {buttonSelection && buttonSelection === ValidationSectionId.WARNING && (
                  <ValidationWarningTable
                  statusSelectOption={statusSelectOption}
                  transactionTypeSelectOption={transactionTypeSelectOption}
                  globalSearch={globalSearch}
                  isFetching={isFetching}
                  setIsFetching={setIsFetching}
                  rowSelection={rowSelection}
                  setRowSelection={setRowSelection}
                  onLoadCb={(info) => {
                    if (info) {
                      setIsLoadingSuccess(true);
                      setHasError(false);
                      setCountFromResponse(info.meta.total_item_count, ValidationSectionId.WARNING);
                    } else {
                      setIsLoadingSuccess(false);
                      setHasError(true);
                      setCountFromResponse(null, ValidationSectionId.WARNING);
                    }
                  }
                }
                />
              )}
              </>
            )}
          </div>
          </div>
      </div>
    </QueryClientProvider>
  );
}

export default ValidationComponent;
